import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { gql, useApolloClient } from '@apollo/client';
import Dropzone from 'react-dropzone';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { UploadFile, VisibilityOutlined, DeleteOutline } from '@mui/icons-material';
import StyledTextField from 'common/theme/fields/StyledTextfield';
import PdfViewModal from 'components/shared/modals/PdfViewModal';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

const DEFAULT_MAX_SIZE = 10000000; // 10MB

// El bucket es privado → para VER un documento ya guardado pedimos una URL temporal presignada.
const SIGNED_DOC_URL = gql`
  query CreateTemporaryUserDocumentUrl($id: ID!, $document_type: String!) {
    createTemporaryUserDocumentUrl(id: $id, document_type: $document_type)
  }
`;

function FileUploadAdapter({
  allowedFileTypes,
  input,
  meta,
  maxSize = DEFAULT_MAX_SIZE,
  viewable = false,
  ...rest
}) {
  const { t } = useTranslation(['buttons', 'errors', 'labels']);
  const dropzoneRef = useRef();
  const dispatch = useDispatch();
  const apollo = useApolloClient();
  const userId = useSelector((state) => state.auth.user?.id);
  const [filename, setFilename] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [signedSrc, setSignedSrc] = useState(null);

  // Cuando el valor es una cadena (URL/ruta de un archivo YA guardado) se puede previsualizar/borrar.
  // Si es un File recién seleccionado, `value` no es string → solo aplica el flujo de subida.
  const existingUrl = viewable && typeof input.value === 'string' && input.value ? input.value : null;

  // Abre el visor con una URL presignada fresca (el campo `input.name` = columna del documento).
  const openViewer = async () => {
    let src = existingUrl;
    if (userId && input.name) {
      try {
        const { data } = await apollo.query({
          query: SIGNED_DOC_URL,
          variables: { id: userId, document_type: input.name },
          fetchPolicy: 'no-cache'
        });
        if (data?.createTemporaryUserDocumentUrl) src = data.createTemporaryUserDocumentUrl;
      } catch {
        // Si falla la firma, intentamos con la URL existente.
      }
    }
    setSignedSrc(src);
    setViewerOpen(true);
  };

  useEffect(() => {
    if ('value' in input && typeof input.value === 'string' && input.value.length > 0) {
      setFilename(input.value.split('/').at(-1));
    }
  }, [input]);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFilename(acceptedFiles[0].name);
      input.onChange(acceptedFiles[0]);
    }
  };

  const onDropRejected = () => {
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({
      key: `upload-file-notification-error`,
      variant: 'error',
      message: t('upload.size.file', { ns: 'errors' })
    })));
  };

  const openDialog = () => {
    if (dropzoneRef.current) dropzoneRef.current.open();
  };

  const clearFile = () => {
    setFilename('');
    input.onChange('');
  };

  return (
    <>
      <Dropzone
        accept={allowedFileTypes}
        ref={dropzoneRef}
        noClick
        noKeyboard
        maxFiles={1}
        maxSize={maxSize}
        multiple={false}
        onDrop={onDrop}
        onDropRejected={onDropRejected}
        {...rest}>
        {({ getRootProps, getInputProps }) => (
          <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 0.5 }} {...getRootProps()}>
            <input {...getInputProps()} />
            <StyledTextField
              label="Outlined"
              variant="outlined"
              placeholder={t('selectAFile', { ns: 'labels' })}
              value={filename}
              error={meta.invalid}
              helperText={meta.invalid ? meta.error : ''}
              sx={{ flexGrow: 1, marginRight: (theme) => theme.spacing(1) }}
              {...rest}
            />
            {existingUrl && (
              <>
                <Tooltip title={t('view', { ns: 'buttons', defaultValue: 'Ver' })}>
                  <IconButton onClick={openViewer} sx={{ height: '47px', color: 'gold.main', '&:hover': { backgroundColor: 'gold.soft' } }}>
                    <VisibilityOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}>
                  <IconButton onClick={clearFile} sx={{ height: '47px', color: (th) => th.palette.sessionStatus.absent.fg }}>
                    <DeleteOutline />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<UploadFile />}
              onClick={openDialog}
              sx={{ height: '47px' }}>
              {t('upload', { ns: 'buttons' })}
            </Button>
          </Box>
        )}
      </Dropzone>
      <PdfViewModal open={viewerOpen} src={signedSrc} title={filename} filename={filename} onClose={() => setViewerOpen(false)} />
    </>
  );
}

FileUploadAdapter.propTypes = {
  allowedFileTypes: PropTypes.object.isRequired,
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
  viewable: PropTypes.bool
};

export default FileUploadAdapter;
